import React, { useState, useEffect } from 'react'
import { Card, ButtonGroup, Tooltip, InputGroup, Intent } from '@blueprintjs/core'
import { Button } from '..'
import { HighlightedText, IconButton, Icon } from '@ui'
import { debounce } from 'lodash'
import api, { IHRLocation } from '@api'
import { Tooltip2 } from '@blueprintjs/popover2'
import css from 'components/NewSettings/settings.module.scss'

interface HRLocationListProps {
  value: IHRLocation | null
  onChange: ([]) => void
  buttonProps?: any
  btnText?: React.ReactNode
  itemDisabled?: (item: IHRLocation) => boolean
  multi?: boolean
  clearable?: boolean
  searchBoxPlaceholder?: string
  values: IHRLocation[]
  cancelLocationFilter: () => void
}

const HRLocationList: React.FC<HRLocationListProps> = ({
  value,
  onChange,
  multi,
  clearable = false,
  values,
  cancelLocationFilter,
}) => {
  const locations = useHRLocations()
  const [query, setQuery] = useState('')
  const [items, setItems] = useState(locations)
  const [showTooltip, setShowTooltip] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [filteredHrLocs, setFilteredHrLocs] = useState<IHRLocation[]>(values)
  const [hoveredItems, setHoveredItems] = useState<{ [key: string]: boolean }>({})
  const itemsPerPage = 25
  const totalPages = Math.ceil(locations.length / itemsPerPage)

  const setItemsByPage = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setItems(locations.slice(startIndex, endIndex))
  }

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1)
  }

  useEffect(() => {
    setItemsByPage()
  }, [locations, currentPage])

  useEffect(() => {
    setFilteredHrLocs(values)
  }, [])

  const search = debounce((query) => {
    setItems(
      query
        ? locations
            .filter((loc) => loc.location.toLowerCase().includes(query.toLowerCase()))
            .slice(0, itemsPerPage)
        : locations.slice(0, itemsPerPage)
    )
  }, 0)

  const onChangeOfLocation = (hrLoc: IHRLocation, flag: string, items: IHRLocation[]) => {
    let hrLocArray: any
    if (flag === 'include') {
      // Include the location
      hrLocArray = [hrLoc]
    } else if (flag === 'exclude') {
      // Exclude the location
      hrLocArray = items.filter(({ id }: { id: string }) => id !== hrLoc.id)
    } else if (flag === 'toggle') {
      // Toggle the location (include if not present, exclude if already present)
      if (filteredHrLocs.find(({ id }) => id === hrLoc.id)) {
        hrLocArray = filteredHrLocs.filter(({ id }: { id: string }) => id !== hrLoc.id)
      } else {
        hrLocArray = [...filteredHrLocs, hrLoc]
      }
    }
    setFilteredHrLocs(hrLocArray)
    setShowTooltip(true)
  }

  const handleHover = (locationId: string, isHovered: boolean) => {
    setHoveredItems((prev) => ({ ...prev, [locationId]: isHovered }))
  }

  const [shownPostDescriptionName, setShownPostDescriptionName] = useState('')

  return (
    <>
      <div style={{ display: 'flex', height: '55vh' }}>
        <div style={{ flex: '1', overflowY: 'auto' }}>
          <InputGroup
            fill={false}
            large
            style={{ width: '50%', margin: '10px 0' }}
            leftElement={
              <div style={{ margin: '15px' }}>
                <Icon name="search" />
              </div>
            }
            onChange={(e) => {
              setQuery(e.target.value)
              search(e.target.value)
            }}
            placeholder="Search"
          />

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {items.map((location) => (
              <Card className={css.hrLocationCard}>
                <div
                  className={css.hrLocationParentDiv}
                  onMouseEnter={() => handleHover(location.id, true)}
                  onMouseLeave={() => handleHover(location.id, false)}>
                  <HighlightedText text={location.location} query={query} />

                  <ButtonGroup fill={false} className="css.hrLocationButtonGroup">
                    <div className={css.hrLocButtonGroupInnerDiv}>
                      <Tooltip2 content="Filter this one only" position="top">
                        <div
                          style={{ visibility: hoveredItems[location.id] ? 'visible' : 'hidden' }}
                          className={css.includeIcon}
                          onClick={() => onChangeOfLocation(location, 'include', [])}>
                          <Icon name="check" />
                        </div>
                      </Tooltip2>

                      <Tooltip2 content="Filter others only" position="top">
                        <div
                          style={{
                            visibility: hoveredItems[location.id] ? 'visible' : 'hidden',
                          }}
                          className={css.excludeIcon}
                          onClick={() => onChangeOfLocation(location, 'exclude', items)}>
                          <Icon name="check" />
                          <Icon name="check" />
                        </div>
                      </Tooltip2>
                    </div>

                    <Tooltip2
                      position="top"
                      content={
                        filteredHrLocs.some((hrLoc) => hrLoc.id === location.id)
                          ? `Hide ${location.location}`
                          : `Show ${location.location}`
                      }>
                      <div onClick={() => onChangeOfLocation(location, 'toggle', [])}>
                        <i
                          className="fe-visible"
                          style={{
                            color: filteredHrLocs.some((hrLoc) => hrLoc.id === location.id)
                              ? '#51b149'
                              : '#b7b7b7',
                            fontSize: '16px',
                          }}
                        />
                      </div>
                    </Tooltip2>
                  </ButtonGroup>
                </div>
              </Card>
            ))}
          </div>
          {!multi && (
            <div className="pagination-buttons">
              <button onClick={goToPreviousPage} disabled={currentPage === 1}>
                Previous
              </button>
              <button onClick={goToNextPage} disabled={currentPage === totalPages}>
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex" style={{ flexDirection: 'column', marginLeft: '10px' }}>
        {showTooltip && (
          <div className="flex">
            {
              <p style={{ color: '#31708f' }}>
                Locations you hide will be hidden from view in all screens, searches & reports
              </p>
            }
            <Button
              className="mla"
              intent={Intent.PRIMARY}
              text="Cancel"
              onClick={cancelLocationFilter}
              type="submit"
            />
            <Button
              onClick={() => {
                onChange(filteredHrLocs)
                setShowTooltip(false)
                cancelLocationFilter()
              }}
              intent={Intent.PRIMARY}
              text="Update"
              type="submit"
            />
          </div>
        )}
      </div>
      {value && clearable && (
        <IconButton
          minimal
          iconName="times"
          tooltip="Clear"
          onClick={() => {
            // onChange(null as unknown as IHRLocation)
          }}
        />
      )}
    </>
  )
}

export default HRLocationList

export function useHRLocations(): IHRLocation[] {
  const [locations, setLocations] = useState<IHRLocation[]>([])

  useEffect(() => {
    api.company.geoLocations().then((geoLocations) => setLocations(geoLocations))
  }, [])

  return locations
}
